import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Phone, MessageCircle } from "lucide-react";
import { SITE } from "@/data/site";
import "@/components/hero.css";

const easeLux = [0.2, 0.9, 0.2, 1];

/**
 * Timing profile for hero copy reveal.
 * Desktop = cinematic slow (matches drone-video pacing).
 * Mobile  = FAST — text appears immediately on load, no waiting for video.
 */
const HERO_TIMING = {
  desktop: {
    line: { d: 1.2, t: 4.8 },
    over: { d: 2.0, t: 5.0 },
    head: { d: 2.6, t: 10.0 },
    sweep: { d: 1.6, t: 25.0 },
    sub:  { d: 2.4, t: 18.0 },
    btns: { d: 1.6, t: 25.0 },
  },
  mobile: {
    line: { d: 0.4, t: 0.05 },
    over: { d: 0.5, t: 0.10 },
    head: { d: 0.6, t: 0.20 },
    sweep:{ d: 0.9, t: 0.90 },
    sub:  { d: 0.55, t: 0.35 },
    btns: { d: 0.55, t: 0.45 },
  },
};

/**
 * Hero — drone video underneath with a static peak image poster on top that
 * fades away as soon as the video begins playing.
 *
 * Layers (same absolute box, no black gap):
 *   z-index 1 : <video> (drone footage)
 *   z-index 2 : <img>   (peak poster — fades to opacity 0 on video play)
 *   z-index 3 : gradient overlays + grain
 *   z-index 10: text content
 */
export default function Hero() {
  const videoRef = useRef(null);
  const [posterFaded, setPosterFaded] = useState(false);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" &&
      window.matchMedia("(max-width: 768px)").matches
  );

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const waHref = `https://wa.me/${SITE.phoneE164}?text=${encodeURIComponent(
    SITE.whatsappMessage
  )}`;

  const Copy = ({ mobile = false }) => {
    const t = mobile ? HERO_TIMING.mobile : HERO_TIMING.desktop;
    return (
    <>
      <motion.div
        initial={{ scaleX: 0, opacity: 0 }}
        animate={{ scaleX: 1, opacity: 1 }}
        transition={{ duration: t.line.d, delay: t.line.t, ease: easeLux }}
        style={{ transformOrigin: mobile ? "center" : "left center" }}
        className={`${
          mobile ? "mx-auto mb-4" : "mx-auto md:mx-0 mb-8"
        } h-[2px] w-24 md:w-32 bg-[var(--pam-gold)]`}
      />
      <motion.p
        initial={{ opacity: 0, y: 20, filter: "blur(8px)" }}
        animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
        transition={{ duration: t.over.d, delay: t.over.t, ease: easeLux }}
        className="overline text-[var(--pam-gold)]"
        data-testid={mobile ? "hero-overline-mobile" : "hero-overline"}
      >
        Trusted Malnad Travel Partner
      </motion.p>
      <motion.h1
        initial={{ opacity: 0, y: 44, filter: "blur(14px)" }}
        animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
        transition={{ duration: t.head.d, delay: t.head.t, ease: easeLux }}
        className={`mt-5 font-serif text-white leading-[0.98] tracking-tight ${
          mobile
            ? "text-4xl sm:text-5xl"
            : "text-5xl sm:text-6xl md:text-7xl lg:text-[104px] max-w-4xl md:max-w-5xl mx-auto md:mx-0"
        }`}
        data-testid={mobile ? "hero-heading-mobile" : "hero-heading"}
      >
        Plan Your{" "}
        <span className="relative inline-block gold-sweep-wrap">
          <span className="italic text-[var(--pam-gold)] relative z-10">
            Malnad Stay.
          </span>
          <motion.span
            aria-hidden="true"
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: t.sweep.d, delay: t.sweep.t, ease: easeLux }}
            style={{ transformOrigin: "left center" }}
            className="gold-sweep-bar absolute left-0 bottom-[6%] h-[10%] w-full bg-[var(--pam-gold)]/35 rounded-sm"
          />
        </span>
      </motion.h1>
      <motion.p
        initial={{ opacity: 0, y: 24, filter: "blur(8px)" }}
        animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
        transition={{ duration: t.sub.d, delay: t.sub.t, ease: easeLux }}
        className={`mt-6 text-white/85 font-light text-base sm:text-lg leading-relaxed ${
          mobile ? "max-w-md mx-auto" : "max-w-2xl mx-auto md:mx-0"
        }`}
        data-testid={mobile ? "hero-subheading-mobile" : "hero-subheading"}
      >
        Handpicked Partner Homestays &amp; Curated Adventures Across Malnad.
      </motion.p>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={
          mobile
            ? { duration: t.btns.d, delay: t.btns.t, ease: easeLux }
            : { duration: t.btns.d, delay: t.btns.t, type: "spring", stiffness: 80, damping: 18 }
        }
        className={`${mobile ? "mt-6" : "mt-10"} flex flex-wrap items-center gap-4 ${
          mobile ? "justify-center" : "justify-center md:justify-start"
        }`}
      >
        <a
          href={waHref}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-gold btn-56 rounded-full text-sm sm:text-base"
          data-testid={mobile ? "hero-book-btn-mobile" : "hero-book-btn"}
        >
          <MessageCircle className="w-4 h-4" />
          Plan Your Malnad Stay
        </a>
        <a
          href="#gallery"
          className="btn-ghost-light btn-56 rounded-full text-sm sm:text-base"
          data-testid={mobile ? "hero-explore-btn-mobile" : "hero-explore-btn"}
        >
          Explore Experiences
        </a>
        <a
          href={`tel:+${SITE.phoneE164}`}
          className="btn-ghost-light btn-56 rounded-full text-sm sm:text-base"
          data-testid={mobile ? "hero-call-btn-mobile" : "hero-call-btn"}
        >
          <Phone className="w-4 h-4" />
          Call to Book
        </a>
      </motion.div>
    </>
    );
  };

  const posterStyle = {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
    objectPosition: "center center",
    zIndex: posterFaded ? -1 : 2,
    opacity: posterFaded ? 0 : 1,
    transition: "opacity 0.5s ease-out",
    willChange: "opacity",
    pointerEvents: "none",
  };
  const videoStyle = {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
    objectPosition: "center",
    zIndex: 1,
    willChange: "transform",
    backfaceVisibility: "hidden",
    transform: "translateZ(0)",
  };

  return (
    <section
      id="top"
      className="hero-shell relative w-full overflow-hidden text-white"
      style={{ minHeight: "80vh", height: "80vh" }}
      data-testid="hero-section"
    >
      {/* ============ DESKTOP LAYOUT ============ */}
      {/* Video (z:1) */}
      <video
        ref={videoRef}
        className="hero-video hero-video-desktop"
        src="/hero-drone.mp4"
        poster="/hero-peak.jpg"
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        decoding="async"
        onPlay={() => setPosterFaded(true)}
        onPlaying={() => setPosterFaded(true)}
        style={videoStyle}
        aria-hidden="true"
        data-testid="hero-video"
      />
      {/* Peak image poster (z:2 → fades to -1 on play) */}
      <img
        src="/hero-peak.jpg"
        alt="Kudremukh peak in the Malnad Western Ghats"
        className="hero-video-desktop"
        style={posterStyle}
        loading="eager"
        fetchPriority="high"
        data-testid="hero-poster-image"
      />
      {/* Premium subtle overlay — light darken only where copy sits, video stays visible */}
      <div
        aria-hidden="true"
        className="hero-overlay-desktop absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0) 30%, rgba(0,0,0,0) 55%, rgba(10,61,42,0.35) 100%)",
          zIndex: 3,
        }}
      />
      <div className="hero-overlay-desktop absolute inset-0 grain" style={{ zIndex: 4, opacity: 0.35 }} />
      <div className="hero-content-desktop relative z-10 container-pam h-full flex-col justify-center md:justify-end pt-32 pb-20 md:pb-24 text-center md:text-left" style={{ zIndex: 10 }}>
        <Copy />
      </div>
      <div
        aria-hidden="true"
        className="hero-bottom-fade absolute bottom-0 left-0 right-0 h-40 pointer-events-none"
        style={{ zIndex: 5 }}
      />

      {/* ============ MOBILE LAYOUT ============ */}
      <div className="hero-shell-mobile w-full">
        <div className="relative w-full" style={{ aspectRatio: "16 / 9" }}>
          <video
            src="/hero-drone.mp4"
            poster="/hero-peak.jpg"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            decoding="async"
            onPlay={() => setPosterFaded(true)}
            className="hero-video absolute inset-0 w-full h-full object-cover"
            style={{ zIndex: 1, willChange: "transform" }}
            data-testid="hero-video-mobile"
          />
          <img
            src="/hero-peak.jpg"
            alt="Kudremukh peak in the Malnad Western Ghats"
            className="absolute inset-0 w-full h-full object-cover"
            style={{
              zIndex: posterFaded ? -1 : 2,
              opacity: posterFaded ? 0 : 1,
              transition: "opacity 0.5s ease-out",
            }}
            data-testid="hero-poster-image-mobile"
          />
        </div>
        <div className="hero-copy-block container-pam">
          <Copy mobile />
        </div>
      </div>
    </section>
  );
}
